<script setup lang="ts">
/**
 * The glyph for a section kind.
 *
 * Drawn inline rather than pulled from an icon font, for three reasons: a font
 * is a network request before anything can be read, its glyphs cannot take the
 * accent colour per state, and a missing font leaves rows of empty boxes.
 *
 * Each shape says what the section *is* rather than decorating it: the table
 * icon has a ruled header, the heading icon is a large bar over a small one.
 * Somebody should be able to read the outline at a glance without the labels.
 */
import { computed } from 'vue'

import type { SectionKind } from '@/domain/section'
import { iconFor } from '@/sections'

const props = withDefaults(
  defineProps<{
    kind: SectionKind
    /** Set for a group whose contents are hidden, which turns the chevron. */
    collapsed?: boolean
    size?: number
  }>(),
  { collapsed: false, size: 14 },
)

/**
 * The paths come from the kind's own definition, so an icon is written once,
 * beside everything else that kind needs, and this file never grows.
 */
const paths = computed(() => iconFor(props.kind))
</script>

<template>
  <svg
    class="icon"
    :class="{ 'icon--turned': props.kind === 'group' && !props.collapsed }"
    :width="props.size"
    :height="props.size"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    stroke-width="1.25"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
  >
    <path v-for="(d, index) in paths" :key="index" :d="d" />
  </svg>
</template>

<style scoped>
.icon {
  flex: none;
  /* Optically centred: these shapes sit slightly high against text. */
  transform: translateY(0.5px);
}
</style>
